import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	ExperienceName,
	REMIX_GENERATE_PREVIEW_MARKS,
	remixGeneratePreviewExperience,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import type { RemixContentComponentProps } from '@atlassian/editor-plugin-malleable-ui/malleableUiPluginType';
import {
	RemixContainer,
	type RemixOptionSelectionParameters,
} from '@atlassian/malleable-ui-components';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';

import type { DynamicUiSource } from './common-types.ts';
import { createRemixContentComponentPlatform } from './EditorRemixCreatorPlatform.tsx';
import { filterRemixFragmentNodes } from './filterRemixFragmentNodes.ts';
import { useRemixPreviewFlow } from './useRemixPreviewFlow.ts';
import { useRemixToAIFC } from './useRemixToAIFC.ts';

/**
 * Creates a content component for RemixContentComponent using RemixContainer.
 *
 * When the `confluence_malleable_ui_platform_remix` feature gate is enabled,
 * delegates to the platform-level RemixContentComponent from
 * @atlassian/malleable-ui-components instead.
 */
export const createRemixContentComponent = () => {
	if (fg('confluence_malleable_ui_platform_remix')) {
		return createRemixContentComponentPlatform();
	}

	const Component = ({
		handleModalClose,
		selectedContext,
		editorApi,
		onStartThinking,
		onStopThinking,
		remixModalMetadata,
		remixSource,
	}: RemixContentComponentProps) => {
		const isRemixCancelled = useRef(false);
		const experienceTracker = useAIMateExperienceTracker();
		const openAIFCStagingModalRef = useRef<() => void>(() => {});
		const selectedSubTypeRef = useRef<string | undefined>(undefined);

		const { dynamicUiType } = useStagingAreaState();
		const { showSmartCreationModal } = useSmartCreationModalState();
		const { openConversationAssistant } = useToggleConversationAssistant();

		const {
			showProgressBar,
			isComplete,
			onCompleteAnimationFinished,
			onActionReady,
			resetCompletion,
		} = useRemixPreviewFlow({
			entryPoint: 'editor',
			experienceTracker,
			dynamicUiType,
			selectedSubTypeRef,
			openAIFCStagingModalRef,
			isRemixCancelledRef: isRemixCancelled,
		});

		const {
			sendAIFCPrompt,
			stopRemixing,
			isRemixError,
			remixStoreError,
			openAIFCStagingModal,
			closeAIFCStagingModal,
			resetRemixConversationId,
		} = useRemixToAIFC({ onActionReady });
		openAIFCStagingModalRef.current = openAIFCStagingModal;

		// Abort experience on unmount (overlay closed without selecting an option).
		// Uses a ref so the cleanup always calls the latest tracker reference.
		const experienceTrackerRef = useRef(experienceTracker);
		experienceTrackerRef.current = experienceTracker;
		useEffect(() => {
			return () => {
				experienceTrackerRef.current.abort({
					name: ExperienceName.REMIX_START,
					reason: 'User closed remix overlay',
					attributes: { reason: 'User closed remix overlay' },
				});
			};
		}, []);

		const onRemixFlowActiveChange = useCallback(
			(active: boolean) => {
				if (active) {
					isRemixCancelled.current = false;
					// Clear stale completion state from any previous flow.
					resetCompletion();
					onStartThinking();
				} else {
					isRemixCancelled.current = true;
					// Abort the current experience before stopping
					experienceTracker.abort({
						name: ExperienceName.REMIX_START,
						reason: 'User cancelled remix thinking',
						attributes: { reason: 'User cancelled remix thinking' },
					});
					remixGeneratePreviewExperience.abort({
						metadata: { reason: 'User cancelled remix thinking' },
					});
					stopRemixing();
					// Start a new experience for the reopened overlay
					experienceTracker.start({
						name: ExperienceName.REMIX_START,
						id: ExperienceName.REMIX_START,
						attributes: {
							entryPoint: 'editor',
							remixSource: 'cancelledRemixThinking',
							originalRemixSource: remixSource,
							product: 'confluence',
						},
					});
					onStopThinking({ reopenModal: true });
				}
			},
			[
				onStartThinking,
				stopRemixing,
				onStopThinking,
				experienceTracker,
				remixSource,
				resetCompletion,
			],
		);

		// Fail experience and clean up thinking UI when Rovo encounters an error
		const prevIsRemixError = useRef(isRemixError);
		useEffect(() => {
			if (!prevIsRemixError.current && isRemixError && !isRemixCancelled.current) {
				const errorMessage = remixStoreError?.content ?? 'Rovo agent error during remix';
				experienceTracker.fail({
					name: ExperienceName.REMIX_START,
					error: new Error(errorMessage),
					attributes: {
						errorType: remixStoreError?.errorType,
						traceId: remixStoreError?.traceId,
					},
				});
				remixGeneratePreviewExperience.failure({
					metadata: {
						errorType: remixStoreError?.errorType,
						traceId: remixStoreError?.traceId,
					},
				});
				onStopThinking({ reopenModal: true });
			}
			prevIsRemixError.current = isRemixError;
		}, [isRemixError, remixStoreError, onStopThinking, experienceTracker]);

		// When an error occurs during remix, close the thinking bar and ephemeral
		// preview, then open the Rovo chat sidebar so the user can see the error.
		useEffect(() => {
			if (!isRemixCancelled.current && isRemixError) {
				// Close the ephemeral preview (staging modal) if it is open
				if (showSmartCreationModal) {
					closeAIFCStagingModal();
				}
				// Close the thinking bar and dismiss the remix UI entirely.
				// This mirrors the renderer's onClose() call which unmounts
				// the component and clears isModalOpen. Using handleModalClose
				// instead of onStopThinking because the first error effect
				// sets isModalOpen=true (via reopenModal), and onStopThinking
				// alone only clears isThinking — leaving the component mounted
				// and the progress bar visible.
				handleModalClose();
				// Open the Rovo chat sidebar to display the error information.
				// `openConversationAssistant` is idempotent (no-op if already
				// open), so it is safe to call on every render where
				// `isRemixError` is true — no infinite toggle loop.
				openConversationAssistant('sidebar');
				resetRemixConversationId();
			}
		}, [
			isRemixError,
			showSmartCreationModal,
			closeAIFCStagingModal,
			handleModalClose,
			openConversationAssistant,
			resetRemixConversationId,
		]);

		const constructDynamicUiSource = useCallback(async (): Promise<DynamicUiSource | undefined> => {
			const localIds = selectedContext?.localIds ?? [];
			const startLocalId = localIds[0] ?? '';
			const endLocalId = localIds[localIds.length - 1] ?? '';
			const fragmentAdf = filterRemixFragmentNodes(selectedContext?.selectionFragment ?? null);

			let fullAdf: DocNode = { type: 'doc', version: 1, content: [] };
			if (editorApi) {
				fullAdf = await new Promise((resolve) => {
					try {
						editorApi.core?.actions?.requestDocument?.(
							(doc: any) => {
								resolve(doc as DocNode);
							},
							{ alwaysFire: true },
						);
					} catch {
						resolve({ type: 'doc', version: 1, content: [] });
					}
				});
			}

			return {
				contentType: 'page' as const,
				startLocalId,
				endLocalId,
				startIndex: selectedContext?.startIndex ?? 0,
				endIndex: selectedContext?.endIndex ?? -1,
				fragmentAdf,
				fullAdf,
			};
		}, [editorApi, selectedContext]);

		const onOptionSelect = useCallback(
			async ({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
				selectedSubTypeRef.current = subType;
				experienceTracker.updateAttributes({
					name: ExperienceName.REMIX_START,
					attributes: { remixType: type, remixSubtype: subType },
				});

				remixGeneratePreviewExperience.start();
				remixGeneratePreviewExperience.mark(REMIX_GENERATE_PREVIEW_MARKS.SUBMIT);
				remixGeneratePreviewExperience.addMetadata({
					product: 'confluence',
					interactionSource: remixModalMetadata?.interactionSource,
					remixType: type,
					remixSubtype: subType,
					entryPoint: 'editor',
				});

				const dynamicUiSource = await constructDynamicUiSource();
				void sendAIFCPrompt({
					userPrompt,
					dynamicUiType: type,
					dynamicUiSubtype: subType,
					dynamicUiSource,
					interactionSource: remixModalMetadata?.interactionSource,
				});
			},
			[constructDynamicUiSource, sendAIFCPrompt, remixModalMetadata, experienceTracker],
		);

		return (
			<RemixContainer
				onClose={handleModalClose}
				onOptionSelect={onOptionSelect}
				onRemixFlowActiveChange={onRemixFlowActiveChange}
				isEphemeralViewVisible={showSmartCreationModal}
				showProgressBar={showProgressBar}
				isComplete={isComplete}
				onCompleteAnimationFinished={onCompleteAnimationFinished}
			/>
		);
	};

	const Wrapper = (props: RemixContentComponentProps) => {
		const { onStopThinking, isThinking, isModalOpen } = props;
		const { showSmartCreationModal } = useSmartCreationModalState();
		const isSmartCreationModalOpen = useRef(false);

		useLayoutEffect(() => {
			// Selected content should remain highlighted throughout the entire flow even when ephemeral modal is open
			// We deselect the content when the ephemeral modal is closed
			if (isSmartCreationModalOpen.current && !showSmartCreationModal && isThinking) {
				onStopThinking();
			}
			isSmartCreationModalOpen.current = showSmartCreationModal;
		}, [showSmartCreationModal, isThinking, onStopThinking]);

		return isModalOpen || isThinking ? (
			<ConfluenceConversationAssistant>
				<Component {...props} />
			</ConfluenceConversationAssistant>
		) : null;
	};

	return Wrapper;
};
